import React from 'react';
import AvatarWithStatus from '../AvatarWithStatus';

// Stream Chat sanitizes user IDs by lowercasing and replacing every char
// outside [a-zA-Z0-9@_-] with an underscore — dots in emails become
// underscores. Re-run the same transform so we can match the current user's
// stream ID back to their email key in forksByEmail.
const toStreamUserId = (email) =>
  (email || '').replace(/[^a-zA-Z0-9@_-]/g, '_').toLowerCase();

// Renders a shared AI chat session card in Stream Chat. Each recipient has
// their own fork — forksByEmail maps recipient email → fork threadId, so the
// "Open in AI Assistant" CTA navigates the current viewer to the copy owned
// by them (not to someone else's fork they can't read).
const AIChatShareMessage = ({ message }) => {
  if (!message.attachments || message.attachments.length === 0) return null;

  const attachment = message.attachments.find(att => att.type === 'ai_session_share');
  if (!attachment) return null;

  const sessionInfo = attachment.sessionInfo || {};
  const forksByEmail = attachment.forksByEmail || {};
  const senderName = attachment.senderName || message.user?.name || 'A teammate';

  const currentStreamId = window.streamChatClient?.user?.id || null;
  const isCurrentUserSender = message.user?.id === currentStreamId;

  // Find the fork threadId owned by the current viewer. The sender sees the
  // original (they still have their own session open) so no CTA for them.
  let myForkThreadId = null;
  if (!isCurrentUserSender && currentStreamId) {
    const target = currentStreamId.toLowerCase();
    for (const [email, forkThreadId] of Object.entries(forksByEmail)) {
      if (toStreamUserId(email) === target) {
        myForkThreadId = forkThreadId;
        break;
      }
    }
  }

  const handleOpen = (event) => {
    event.preventDefault();
    const threadId = isCurrentUserSender ? sessionInfo.originalThreadId : myForkThreadId;
    if (!threadId) {
      alert("Couldn't find your copy of this shared chat.");
      return;
    }
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', {
        detail: { expanded: true, threadId }
      }));
    }, 150);
  };

  const formattedTime = new Date(attachment.sharedAt || message.created_at).toLocaleString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const messageCount = sessionInfo.messageCount || 0;
  const messageCountLabel = messageCount > 0
    ? `${messageCount} message${messageCount !== 1 ? 's' : ''}`
    : 'AI conversation';

  return (
    <div className={`flex ${isCurrentUserSender ? 'justify-end' : 'justify-start'} mb-4 group`}>
      {!isCurrentUserSender && (
        <div className="mr-3 mt-1 flex-shrink-0">
          <AvatarWithStatus user={message.user} size={32} showStatus={true} />
        </div>
      )}
      <div className="flex flex-col max-w-sm">
        {!isCurrentUserSender && (
          <div className="text-xs font-medium text-gray-600 mb-1 ml-1">{senderName}</div>
        )}

        <div className={`rounded-xl overflow-hidden shadow-sm border ${
          isCurrentUserSender ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200'
        }`}>
          <div className={`px-4 py-3 border-b ${
            isCurrentUserSender ? 'border-orange-200' : 'border-gray-200'
          } flex items-center`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
              isCurrentUserSender ? 'bg-orange-200' : 'bg-gray-100'
            }`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isCurrentUserSender ? '#ea580c' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
              </svg>
            </div>
            <div className="flex-1">
              <h3 className={`font-medium text-sm ${
                isCurrentUserSender ? 'text-orange-900' : 'text-gray-800'
              }`}>
                {sessionInfo.hotelName || 'Shared AI Chat'}
              </h3>
              <p className={`text-xs ${
                isCurrentUserSender ? 'text-orange-700' : 'text-gray-500'
              }`}>
                {messageCountLabel}
              </p>
            </div>
          </div>

          {sessionInfo.lastMessage && (
            <div className={`px-4 py-3 text-xs border-b ${
              isCurrentUserSender ? 'border-orange-200 text-orange-900' : 'border-gray-200 text-gray-700'
            }`} style={{ maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {sessionInfo.lastMessage}
            </div>
          )}

          <div className="p-3">
            <button
              onClick={handleOpen}
              disabled={!isCurrentUserSender && !myForkThreadId}
              className={`w-full py-2 px-3 text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center justify-center ${
                (!isCurrentUserSender && !myForkThreadId)
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3"></path>
              </svg>
              {isCurrentUserSender ? 'Open original chat' : (myForkThreadId ? 'Open in AI Assistant' : 'Not available for you')}
            </button>
          </div>
        </div>

        <div className={`flex items-center mt-1 text-xs text-gray-400 ${
          isCurrentUserSender ? 'justify-end mr-1' : 'ml-1'
        }`}>
          <span>{formattedTime}</span>
        </div>
      </div>
    </div>
  );
};

export default AIChatShareMessage;
